import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { CheckIcon } from '@fiverr-private/icons';
import { Checkbox } from '@fiverr-private/inputs';
import { grey_1100 } from '@fiverr-private/sass/helpers/index';
import type { ItemValue } from '../../Dropdown/useDropdownState/types';
import useDropdownContext from '../../Dropdown/useDropdownContext';
import useMenuContext from '../useMenuContext';
import useMenuGroupContext from '../useMenuGroupContext';
import type { MenuItemProps } from './types';
import { DEFAULT_ITEM_NAME } from './constants';
import styles from './index.module.scss';

const MenuItem = ({ children, value, selectable, disabled = false, href, target, onClick }: MenuItemProps) => {
  const itemRef = useRef<HTMLLIElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  const { onChange, values } = useDropdownContext();
  const { addMenuItem, removeMenuItem, closeMenu } = useMenuContext();
  const { isMulti, name = DEFAULT_ITEM_NAME } = useMenuGroupContext();

  const isChecked = useMemo(
    () =>
      Boolean(
        isMulti ? (values[name] as ItemValue[])?.includes(value?.toString()) : values[name] === value?.toString()
      ),
    [isMulti, values, name, value]
  );

  const handleOnMouseEnter = useCallback(() => itemRef.current?.focus(), []);

  const handleOnFocus = useCallback(() => setIsFocused(true), []);

  const handleOnBlur = useCallback(() => setIsFocused(false), []);

  const handleOnChange = useCallback(
    ({ currentTarget }: React.ChangeEvent<HTMLInputElement>) => onChange(currentTarget),
    [onChange]
  );

  const handleOnClick = useCallback(
    (event: React.MouseEvent<HTMLLIElement>) => {
      if (disabled) {
        return;
      }

      closeMenu();
      onClick?.(event);
    },
    [closeMenu, disabled, onClick]
  );

  const toggleValue = useCallback(() => {
    if (selectable && !disabled) {
      const { name, value, type, checked } = inputRef.current as HTMLInputElement;
      onChange({ name, value, type, checked: !checked });
    }
  }, [onChange, selectable, disabled]);

  useEffect(() => {
    if (selectable) {
      addMenuItem({
        ref: itemRef,
        type: isMulti ? 'checkbox' : 'radio',
        value: value?.toString(),
        name,
        toggleValue,
        disabled,
      });
    } else {
      addMenuItem({
        ref: itemRef,
        name,
        disabled,
      });
    }

    return () => {
      removeMenuItem(itemRef);
    };
  }, [isMulti, selectable, disabled, value, name, addMenuItem, removeMenuItem, toggleValue]);

  const shouldUseAnchorElement = href && !disabled;

  const MenuItemWrapper = shouldUseAnchorElement ? 'a' : React.Fragment;
  const menuItemWrapperProps = shouldUseAnchorElement ? { href, target, className: styles['no-decoration-link'] } : {};

  return (
    <MenuItemWrapper {...menuItemWrapperProps}>
      <li
        className={classNames(styles.container, { [styles.focused]: isFocused, [styles.disabled]: disabled })}
        onMouseEnter={handleOnMouseEnter}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        tabIndex={disabled ? -1 : 0}
        ref={itemRef}
        onClick={handleOnClick}
        role="listitem"
        aria-disabled={disabled}
      >
        {selectable && !isMulti && (
          <label className={styles.option}>
            <input
              name={name}
              value={value}
              type="radio"
              role="radio"
              checked={isChecked}
              onChange={handleOnChange}
              tabIndex={-1}
              ref={inputRef}
            />
            <div className={styles.icon} role="img">
              {isChecked && <CheckIcon color={grey_1100} />}
            </div>
            <div className={styles.content}>{children}</div>
          </label>
        )}
        {selectable && isMulti && (
          <Checkbox
            className={styles.option}
            name={name}
            value={value}
            checked={isChecked}
            onChange={handleOnChange}
            tabIndex={-1}
            disabled={disabled}
            ref={inputRef}
          >
            <div className={styles.content}>{children}</div>
          </Checkbox>
        )}
        {!selectable && <div className={styles.option}>{children}</div>}
      </li>
    </MenuItemWrapper>
  );
};

export default MenuItem;
